import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Bell, CheckCircle2, Sparkles, Inbox } from "lucide-react";

export default function Notifications() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) api.get("/notifications")
      .then(({ data }) => setNotifs(Array.isArray(data) ? data : []))
      .catch(() => setNotifs([]));
  }, [user]);

  const handleRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setNotifs((xs) => xs.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  if (!user) return null;

  return (
    <AppShell>
      <div className="mb-8">
        <p className="caption-label text-[#C68E59]">Notifications</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Vos alertes</h1>
      </div>

      {notifs.length === 0 && (
        <div className="bg-white rounded-2xl border border-[#EADDC9] p-12 text-center text-[#8B8680]">
          <Bell className="w-8 h-8 mx-auto mb-3 text-[#C4C4C4]" />
          Aucune notification pour l'instant.
        </div>
      )}

      <div className="space-y-3">
        {notifs.map((n) => {
          const Icon =
            n.type === "application_accepted" ? CheckCircle2 : n.type === "new_application" ? Inbox : Sparkles;
          const accent =
            n.type === "application_accepted"
              ? "bg-[#E7F5EC] text-[#2E7D32]"
              : n.type === "new_application"
              ? "bg-[#EEF3FF] text-[#3B5BDB]"
              : "bg-[#F5E6D0] text-[#C68E59]";
          return (
            <div
              key={n.id}
              className={`bg-white rounded-2xl border p-5 flex items-start gap-4 ${
                !n.read ? "border-[#1A1A1A]/20" : "border-[#EADDC9]"
              }`}
              data-testid={`notif-${n.id}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accent}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-serif text-xl text-[#1A1A1A] leading-tight">{n.title}</p>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#3B5BDB]" />}
                </div>
                <p className="text-sm text-[#666] mt-1">{n.body}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-[#8B8680]">
                  <span>{new Date(n.created_at).toLocaleString("fr-FR")}</span>
                  {n.link && (
                    <Link to={n.link} className="text-[#1A1A1A] hover:underline font-semibold">
                      Voir
                    </Link>
                  )}
                  {!n.read && (
                    <button
                      onClick={() => handleRead(n.id)}
                      className="text-[#1A1A1A] hover:underline"
                    >
                      Marquer comme lu
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
